"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  Users, 
  MessagesSquare, 
  Calendar, 
  Rocket, 
  Lock,
  ArrowRight,
  Globe,
  Sparkles
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import Link from "next/link";

const features = [
  {
    icon: <MessagesSquare className="w-6 h-6" />,
    title: "The Tribe Channel",
    description: "Access to our private community channels where founders, creators, and professionals collaborate daily."
  },
  {
    icon: <Calendar className="w-6 h-6" />,
    title: "Alumni-Only Meetups",
    description: "Invites to exclusive city-based meetups and reunions to keep the connection alive after the trip."
  },
  {
    icon: <Rocket className="w-6 h-6" />,
    title: "Growth Opportunities",
    description: "Get first preference for job opportunities, partnership deals, and mentor office hours within the network."
  }
];

const AlumniPage = () => {
  return (
    <div className="bg-white min-h-screen font-jakarta">
      {/* Hero Section */}
      <section className="bg-gray-50 py-24 px-6 border-b border-gray-100 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#FFAE00]/5 -skew-x-12 translate-x-1/2 pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl space-y-8"
          >
            <div className="inline-flex items-center gap-2 bg-[#063258] text-[#FFAE00] px-6 py-2 rounded-full font-bold">
              <Lock className="w-4 h-4" /> ALUMNI NETWORK
            </div>
            <h1 className="text-5xl md:text-7xl font-bricolage font-bold text-[#063258] leading-tight">
              The journey doesn&apos;t <span className="text-[#FFAE00]">end at the checkout.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
              Welcome back, Traveller. Our Alumni Network is a curated space for those who have experienced an Outcome Outing. It&apos;s where the real magic happens.
            </p>
            <div className="pt-4">
              <Link href="https://community.outcomeouting.com" target="_blank">
                <Button className="bg-[#FFAE00] hover:bg-orange-500 text-white px-10 py-7 rounded-2xl text-xl font-bold shadow-xl flex items-center gap-3 transition-transform hover:scale-105">
                  Enter Alumni Portal <ArrowRight />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {features.map((feature, idx) => (
            <div key={idx} className="space-y-6 group">
              <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center text-[#063258] group-hover:bg-[#063258] group-hover:text-[#FFAE00] transition-all duration-300">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-bricolage font-bold text-[#063258]">{feature.title}</h3>
              <p className="text-gray-600 text-lg leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Exclusive Access Stats */}
      <section className="py-24 px-6 bg-[#063258] text-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="relative">
            <div className="grid grid-cols-2 gap-6">
              {[
                { label: "Community Members", val: "2000+" },
                { label: "Collabs Done", val: "150+" },
                { label: "Cities Active", val: "12+" },
                { label: "Mentors Active", val: "40+" }
              ].map((stat, i) => (
                <div key={i} className="bg-white/5 border border-white/10 p-8 rounded-3xl text-center backdrop-blur-sm">
                  <p className="text-3xl font-bricolage font-bold text-[#FFAE00] mb-2">{stat.val}</p>
                  <p className="text-xs font-bold uppercase tracking-widest opacity-60 font-jakarta">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-8">
            <h2 className="text-4xl md:text-5xl font-bricolage font-bold">More than a directory. <br/><span className="text-[#FFAE00]">A support system.</span></h2>
            <p className="text-xl text-white/70 leading-relaxed">
              Whether you&apos;re looking for a co-founder, a second opinion on your product, or just someone to grab coffee with in a new city—the Outcome Outing alumni are always just a ping away.
            </p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3"><Sparkles className="text-[#FFAE00] w-5 h-5" /> <span>Member-only discounts on future trips</span></li>
              <li className="flex items-center gap-3"><Sparkles className="text-[#FFAE00] w-5 h-5" /> <span>Priority access to sold-out cohorts</span></li>
              <li className="flex items-center gap-3"><Sparkles className="text-[#FFAE00] w-5 h-5" /> <span>Referral earning bonuses</span></li>
            </ul>
          </div>
        </div>
      </section>

      {/* Access Request */}
      <section className="py-24 px-6 text-center">
        <h2 className="text-3xl font-bricolage font-bold text-[#063258] mb-8">Haven&apos;t joined a trip yet?</h2>
        <p className="text-xl text-gray-500 mb-12 max-w-2xl mx-auto">
          The alumni network is exclusive to those who have completed at least one Outcome Outing. Join our next cohort to get access.
        </p>
        <Link href="/trails">
          <Button variant="outline" className="border-[#063258] text-[#063258] px-10 py-7 rounded-2xl text-xl font-bold flex items-center gap-2 mx-auto hover:bg-gray-50">
            Browse Upcoming Trips <ArrowRight className="w-5 h-5" />
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default AlumniPage;
